import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Container,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Home as HomeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const BookingConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { 
    success, 
    message, 
    bookingData, 
    salonData 
  } = location.state || {};

  // Si pas de données, rediriger vers l'accueil
  if (!message) {
    navigate('/');
    return null;
  }

  const handleGoHome = () => {
    navigate('/');
  };

  const handleCallSalon = () => {
    if (salonData?.phone) {
      window.location.href = `tel:${salonData.phone}`;
    }
  };

  const handleEmailSalon = () => {
    if (salonData?.email) {
      window.location.href = `mailto:${salonData.email}`;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8F9FA', py: 4 }}>
      <Container maxWidth="sm">
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {success ? (
              <>
                <CheckCircleIcon 
                  sx={{ 
                    fontSize: 80, 
                    color: '#16a34a', 
                    mb: 2 
                  }} 
                />
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, color: '#16a34a' }}>
                  {t('booking.bookingConfirmed')}
                </Typography>
              </>
            ) : (
              <>
                <ErrorIcon 
                  sx={{ 
                    fontSize: 80, 
                    color: '#dc2626', 
                    mb: 2 
                  }} 
                />
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, color: '#dc2626' }}>
                  {t('booking.bookingError')}
                </Typography>
              </>
            )}

            <Alert 
              severity={success ? "success" : "error"} 
              sx={{ mb: 3, textAlign: 'left' }}
            >
              <Typography variant="body1">
                {message}
              </Typography>
            </Alert>

            {success && bookingData && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ textAlign: 'left', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {t('booking.bookingDetails')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>{t('booking.salon')} :</strong> {bookingData.salonName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('booking.service')} :</strong> {bookingData.service}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('booking.date')} :</strong> {dayjs(bookingData.date).format('dddd DD MMMM YYYY')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('booking.time')} :</strong> {dayjs(bookingData.date).format('HH:mm')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('booking.client')} :</strong> {bookingData.clientName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('booking.phone')} :</strong> {bookingData.clientPhone}
                    </Typography>
                    {bookingData.clientEmail && (
                      <Typography variant="body2">
                        <strong>{t('booking.email')} :</strong> {bookingData.clientEmail}
                      </Typography>
                    )}
                    {bookingData.notes && (
                      <Typography variant="body2">
                        <strong>{t('booking.notes')} :</strong> {bookingData.notes}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {(salonData?.phone || salonData?.email) && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
                      {t('booking.needToContactSalon')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                      {salonData?.phone && (
                        <Button
                          variant="outlined"
                          startIcon={<PhoneIcon />}
                          onClick={handleCallSalon}
                          sx={{
                            borderColor: '#F5F5DC',
                            color: '#000000',
                            '&:hover': {
                              backgroundColor: '#F5F5DC',
                              borderColor: '#E8E8D0'
                            }
                          }}
                        >
                          Appeler
                        </Button>
                      )}
                      {salonData?.email && (
                        <Button
                          variant="outlined"
                          startIcon={<EmailIcon />}
                          onClick={handleEmailSalon}
                          sx={{
                            borderColor: '#F5F5DC',
                            color: '#000000',
                            '&:hover': {
                              backgroundColor: '#F5F5DC',
                              borderColor: '#E8E8D0'
                            }
                          }}
                        >
                          Email
                        </Button>
                      )}
                    </Box>
                  </>
                )}
              </>
            )}

            <Box sx={{ mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                sx={{
                  backgroundColor: '#000000',
                  color: '#FFFFFF',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: '#1F1F1F'
                  }
                }}
              >
                Retour à l'accueil
              </Button>
            </Box>

            {success && (
              <Typography variant="body2" sx={{ mt: 3, color: '#6B7280' }}>
                Vous recevrez une confirmation par email si vous avez fourni votre adresse email.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default BookingConfirmation;